import {
  Button,
  DataLoadingWrapper,
  Drawer,
  InteractiveContainer,
  PlainButton,
} from "@jugl-web/ui-components";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  useRestApiProvider,
  WhatsappTemplateCategory,
  WhatsappTemplateStatus,
} from "@jugl-web/rest-api";
import { useInView } from "react-intersection-observer";
import { useTranslations } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { WhatsappTemplateModel } from "@jugl-web/rest-api/integrations/models";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { TemplateTile } from "./components/TemplateTile";
import { WhatsappAccountTab } from "../ManageAccountsDrawer/screens/WhatsappAccountScreen/types";
import { useManageAccountsDrawer } from "../ManageAccountsDrawer/providers/ManageAccountsDrawerContext";

type PageState = {
  requestId: number;
  nextPageId?: string;
  hasMore?: boolean;
};

export const WhatsappTemplatesSidebar: React.FC<{
  isOpen: boolean;
  onRequestClose: () => void;
  isLoading?: boolean;
  onSelectTemplate: (template: WhatsappTemplateModel) => void;
}> = ({
  isOpen,
  onRequestClose,
  onSelectTemplate,
  isLoading: isSidebarLoading,
}) => {
  const { integrationsApi } = useRestApiProvider();
  const { openManageAccountDrawer } = useManageAccountsDrawer();
  const { entityId } = useEntitySelectedProvider();
  const { t } = useTranslations();
  const [getWhatsappTemplates, { isLoading, isError, isFetching }] =
    integrationsApi.useLazyGetWhatsappTemplatesQuery();
  const [selectedTemplate, setSelectedTemplate] = useState<string>();

  const currentPageState = useRef<PageState | null>(null);
  const [templates, setTemplates] = useState<WhatsappTemplateModel[]>([]);

  const { ref: inViewRef, inView } = useInView();

  const loadNextPage = useCallback(async () => {
    let nextPageId: string | undefined;
    if (currentPageState.current) {
      nextPageId = currentPageState.current.nextPageId;
    }

    const nextPageState: PageState = {
      requestId: Math.random(),
      nextPageId,
    };

    currentPageState.current = nextPageState;

    const result = await getWhatsappTemplates({
      entityId,
      params: {
        page: nextPageId,
        type: WhatsappTemplateCategory.message,
        status: WhatsappTemplateStatus.approved,
        enabled: true,
      },
    });

    if (currentPageState.current?.requestId !== nextPageState.requestId) {
      return;
    }

    if ("error" in result && result.error) {
      currentPageState.current = nextPageState;
      return;
    }

    if ("data" in result && result.data && result.data.data) {
      const responseData = result.data.data;
      setTemplates((prev) =>
        nextPageId ? [...prev, ...responseData] : responseData
      );

      nextPageState.hasMore = !!result.data.next_page;
      nextPageState.nextPageId = result.data.next_page || undefined;
      currentPageState.current = nextPageState;
    }
  }, [entityId, getWhatsappTemplates]);

  useEffect(() => {
    if (inView) {
      loadNextPage();
    }
  }, [inView, loadNextPage]);

  const handleReset = useCallback(() => {
    currentPageState.current = null;
    loadNextPage();
  }, [loadNextPage]);

  useEffect(() => {
    if (isOpen) {
      handleReset();
      return;
    }

    setTemplates([]);
    setSelectedTemplate(undefined);
  }, [isOpen, handleReset]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onRequestClose}
      placement="right"
      className="flex h-full w-[640px] flex-col"
    >
      <div className="flex items-center justify-between border-0 border-b border-solid border-dark-100 px-6 py-[14px]">
        <div className="flex flex-col gap-1">
          <span className="font-secondary text-sm font-semibold text-[#383838]">
            {t({
              id: "client-support-page.send-whatssap-template",
              defaultMessage: "Send WhatsApp Template",
            })}
          </span>
          <span className="font-secondary text-[13px] leading-2 text-grey-900">
            {t({
              id: "client-support-page.select-from-available-message-templates",
              defaultMessage: `Select from available “Message Templates”`,
            })}
          </span>
        </div>
        <PlainButton onClick={onRequestClose}>
          <CloseIcon />
        </PlainButton>
      </div>
      <div className="jugl__custom-scrollbar mt-4 flex flex-col gap-1.5 overflow-y-auto px-8">
        <DataLoadingWrapper
          isError={isError}
          isFetching={isFetching}
          isLoading={isLoading || (isFetching && templates.length === 0)}
          onRetry={loadNextPage}
        >
          {templates.length ? (
            templates.map((template) => (
              <TemplateTile
                key={template.id}
                {...template}
                isSelected={selectedTemplate === template.id}
                onSelect={() => setSelectedTemplate(template.id)}
              />
            ))
          ) : (
            <div className="mt-40 flex flex-col items-center justify-center gap-2 text-center font-secondary text-sm leading-[160%]">
              <span className="text-[#828282]">
                {t({
                  id: "client-support-page.no-message-templates-was-created-yet",
                  defaultMessage: "No Message Templates was created yet",
                })}
              </span>
              <InteractiveContainer
                className="text-primary-800 underline"
                onClick={() => {
                  onRequestClose();
                  openManageAccountDrawer({
                    name: "whatsappAccount",
                    tab: WhatsappAccountTab.messageTemplates,
                  });
                }}
              >
                {t({
                  id: "client-support-page.go-to-message-templated",
                  defaultMessage: "Go to ‘Message Templates’",
                })}
              </InteractiveContainer>
            </div>
          )}
          {currentPageState.current?.hasMore && !isLoading && !isFetching && (
            <div ref={inViewRef} />
          )}
        </DataLoadingWrapper>
      </div>
      <div className="mt-auto flex justify-center py-8">
        <Button
          className="h-10 w-[300px]"
          isDisabled={!selectedTemplate || isSidebarLoading}
          onClick={() => {
            const selectedTemplateItem = templates.find(
              (template) => template.id === selectedTemplate
            );
            if (selectedTemplateItem) {
              onSelectTemplate(selectedTemplateItem);
              onRequestClose();
            }
          }}
        >
          {t({
            id: "common.select",
            defaultMessage: `Select”`,
          })}
        </Button>
      </div>
    </Drawer>
  );
};
